import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  X, Loader2, RefreshCw, Send, ExternalLink, Mail, Clock, AlertTriangle,
  Eye, MousePointerClick, CheckCircle2, XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatRel } from "./constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_PILL = {
  pending: { tone: "bg-slate-100 text-slate-700 ring-slate-200", icon: Clock },
  sent: { tone: "bg-cyan-50 text-cyan-700 ring-cyan-200", icon: Send },
  delivered: { tone: "bg-emerald-50 text-emerald-700 ring-emerald-200", icon: CheckCircle2 },
  opened: { tone: "bg-emerald-100 text-emerald-800 ring-emerald-300", icon: Eye },
  clicked: { tone: "bg-amber-50 text-amber-700 ring-amber-200", icon: MousePointerClick },
  bounced: { tone: "bg-rose-50 text-rose-700 ring-rose-200", icon: AlertTriangle },
  complained: { tone: "bg-rose-100 text-rose-800 ring-rose-300", icon: AlertTriangle },
  failed: { tone: "bg-rose-50 text-rose-700 ring-rose-200", icon: XCircle },
  delayed: { tone: "bg-amber-50 text-amber-700 ring-amber-200", icon: Clock },
};

const EVENT_ICON = {
  "email.sent": Send,
  "email.delivered": CheckCircle2,
  "email.delivery_delayed": Clock,
  "email.opened": Eye,
  "email.clicked": MousePointerClick,
  "email.bounced": AlertTriangle,
  "email.complained": AlertTriangle,
  "email.failed": XCircle,
};

export default function OutreachInboxDrawer({ logId, onClose, onResent }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axios.get(`${API}/admin/crm/outreach-inbox/${logId}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Failed to load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [logId]);

  const resend = async () => {
    if (!window.confirm("Re-send this outreach to the same contact?")) return;
    setResending(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/crm/outreach-inbox/${logId}/resend`);
      toast.success("Resent");
      if (onResent) onResent(r);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setResending(false);
    }
  };

  const log = data?.log;
  const events = data?.events || [];
  const meta = log ? (STATUS_PILL[log.status] || STATUS_PILL.sent) : null;
  const StatusIcon = meta?.icon;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex justify-end"
      onClick={onClose}
      data-testid="crm-inbox-drawer"
    >
      <aside
        className="bg-white w-full max-w-2xl h-full overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-br from-slate-50 to-white flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4 text-cyan-700" />
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                Outreach email
              </span>
              {log && (
                <span className={`text-xs px-2 py-0.5 rounded-md ring-1 inline-flex items-center gap-1 ${meta.tone}`}>
                  {StatusIcon && <StatusIcon className="w-3 h-3" />}
                  {log.status}
                </span>
              )}
            </div>
            <h2 className="text-lg font-bold text-slate-900 font-['Plus_Jakarta_Sans'] truncate" data-testid="crm-inbox-drawer-subject">
              {log?.subject || "Loading…"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 flex-shrink-0"
            data-testid="crm-inbox-drawer-close"
            aria-label="Close drawer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
          ) : !log ? (
            <div className="text-sm text-slate-500">Not found.</div>
          ) : (
            <>
              {/* Meta */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Field label="To">
                  <a href={`mailto:${log.to_email}`} className="text-cyan-700 hover:underline">{log.to_email}</a>
                </Field>
                <Field label="From">
                  <span className="font-mono text-xs">{log.from_email || "—"}</span>
                </Field>
                <Field label="Channel"><span className="text-slate-700">{log.channel || "email"}</span></Field>
                <Field label="Sent at">
                  <span className="text-slate-700">{log.created_at ? new Date(log.created_at).toLocaleString() : "—"}</span>
                </Field>
                <Field label="Provider message ID">
                  <span className="font-mono text-[11px] text-slate-600 break-all">{log.provider_message_id || "—"}</span>
                </Field>
                <Field label="Sent by">
                  <span className="text-slate-700">{log.sent_by || "—"}</span>
                </Field>
              </div>

              {log.contact && (
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 flex items-center justify-between" data-testid="crm-inbox-drawer-contact-card">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Contact</div>
                    <div className="font-semibold text-slate-900">{log.contact.name}</div>
                    <div className="text-xs text-slate-500">
                      {log.contact.organization || "—"} · {log.contact.category}
                      {typeof log.contact.lead_score === "number" && (
                        <span className="ml-2 text-slate-400">· score {log.contact.lead_score}/100</span>
                      )}
                    </div>
                  </div>
                  <Link
                    to={`/admin/crm/contacts/${log.crm_contact_id}`}
                    className="text-xs text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1"
                    data-testid="crm-inbox-drawer-contact-link"
                  >
                    Open <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              )}

              {log.error_message && (
                <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-800" data-testid="crm-inbox-drawer-error">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-700 mb-1">Error</div>
                  {log.error_message}
                </div>
              )}

              {/* Body */}
              <div data-testid="crm-inbox-drawer-body">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Body</div>
                <div className="bg-white border border-slate-200 rounded-md p-4 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                  {log.body || <span className="text-slate-400">(no body)</span>}
                </div>
              </div>

              {/* Webhook events timeline */}
              <div data-testid="crm-inbox-drawer-events">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center justify-between">
                  <span>Webhook events ({events.length})</span>
                  {log.last_webhook_at && (
                    <span className="text-slate-400 normal-case tracking-normal text-xs">
                      Last: {formatRel(log.last_webhook_at)}
                    </span>
                  )}
                </div>
                {events.length === 0 ? (
                  <div className="text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-md p-3 text-center">
                    No webhook events yet. Resend delivery/open/click/bounce events will appear here as they arrive.
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {events.map((ev) => {
                      const Icon = EVENT_ICON[ev.event_type] || Mail;
                      return (
                        <li
                          key={ev.id}
                          className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 flex items-start gap-3"
                          data-testid={`crm-inbox-drawer-event-${ev.id}`}
                        >
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-white ring-1 ring-slate-200 flex items-center justify-center mt-0.5">
                            <Icon className="w-3.5 h-3.5 text-slate-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-800">{ev.event_type}</div>
                            <div className="text-xs text-slate-500">{ev.received_at ? new Date(ev.received_at).toLocaleString() : "—"}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {log && (
          <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex flex-wrap gap-2 justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={resend}
              disabled={resending || !log.crm_contact_id}
              data-testid="crm-inbox-drawer-resend"
              title={!log.crm_contact_id ? "Original contact was deleted — cannot resend" : ""}
            >
              {resending ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-1.5" />
              )}
              Re-send
            </Button>
            <Button
              size="sm"
              onClick={onClose}
              className="bg-slate-800 hover:bg-slate-900 text-white"
              data-testid="crm-inbox-drawer-done"
            >
              Done
            </Button>
          </div>
        )}
      </aside>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
